import { AnyAction, PayloadAction } from '@reduxjs/toolkit';
import { call, put, takeEvery } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';
import { Loading } from '../../../../../base/enums';
import { appActions } from '../../../../../base/redux/slices/appSlice';
import { isEmptyObject, timestampWithNoMillisecondsToDate } from '../../../../../base/utils';
import { __t } from '../../../../../base/utils/t';
import { vacancyApi } from '../../api';
import { VacancyResponseDTO } from '../../api/interfaces';
import { RecommendationsWithProfiles, UpgradePackage } from '../../interfaces';
import { isTagChecked, mapTagIdToCheckbox } from '../../utils';
import { vacancyActions } from '../slices/vacancySlice';
import { ApiResponse, ApiError } from '../../../../../base/api';
import { workerApi } from '../../../../../worker/api';
import { baseErrorHandler } from '../../../../../base/redux/sagas/appSaga';

function* init() {
    try {
        yield put(appActions.setLoading(Loading.App));

        const { data }: ApiResponse<UpgradePackage[]> = yield call(vacancyApi.getTarrifs);
        yield put(vacancyActions.getVacancyUpgradePackagesSuccess(data));

        yield put(vacancyActions.initSuccess());
    } catch (e) {
        yield call(baseErrorHandler, e);
    }
}

function* getVacancyData({ payload: vacancyId }: AnyAction) {
    try {
        yield put(appActions.setLoading(Loading.App));

        const {
            data: { employer, responses, recommendations, ...data },
        }: ApiResponse<VacancyResponseDTO> = yield call(vacancyApi.getVacancyDataById, vacancyId);
        const convertedTags = mapTagIdToCheckbox(data.tagIds);
        const convertedData = {
            id: vacancyId,
            name: data.name,
            forRecruiters: data.forRecruiters,
            forWorkers: data.forWorkers,
            suitableForMen: isTagChecked(convertedTags, 'suitableForMen'),
            suitableForWomen: isTagChecked(convertedTags, 'suitableForWomen'),
            suitableForCouple: isTagChecked(convertedTags, 'suitableForCouple'),
            suitableForStudents: isTagChecked(convertedTags, 'suitableForStudents'),
            rateType: data.rateType,
            rateFrom: data.rateFrom ? data.rateFrom.toString() : '',
            rateTo: data.rateTo ? data.rateTo.toString() : '',
            hoursPerMonthFrom: data.hoursPerMonthFrom ? data.hoursPerMonthFrom.toString() : '',
            hoursPerMonthTo: data.hoursPerMonthTo ? data.hoursPerMonthTo.toString() : '',
            currencyId: data.currencyId,
            isHotVacancy: isTagChecked(convertedTags, 'isHotVacancy'),
            isExperienceNeeded: isTagChecked(convertedTags, 'isExperienceNeeded'),
            isPassportOnly: isTagChecked(convertedTags, 'isPassportOnly'),
            isAccomodationProvided: isTagChecked(convertedTags, 'isAccomodationProvided'),
            isFoodProvided: isTagChecked(convertedTags, 'isFoodProvided'),
            isTransferProvided: isTagChecked(convertedTags, 'isTransferProvided'),
            isClothesProvided: isTagChecked(convertedTags, 'isClothesProvided'),
            isSeasonalWork: isTagChecked(convertedTags, 'isSeasonalWork'),
            isRemoteWork: isTagChecked(convertedTags, 'isRemoteWork'),
            isQuickApplyAvailable: isTagChecked(convertedTags, 'isQuickApplyAvailable'),
            city: data.city,
            description: data.description,
            images: data.images,
            requiredLanguageId: data.requiredLanguageId,
            specialities: data.specialities,
            areas: [data.area],
            workersCount: data.workersCount ? data.workersCount.toString() : '1',
            views: data.views || null,
            updatedAt: data.updatedAt ? timestampWithNoMillisecondsToDate(data.updatedAt) : null,
            isActive: data.isActive || false,
        };

        yield put(vacancyActions.setEmployerData(employer));
        yield put(vacancyActions.setRecommendations(recommendations));
        yield put(vacancyActions.setResponses(responses));
        yield put(vacancyActions.getVacancyDataSuccess(convertedData));
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* closeVacancy({ payload: vacancyId }: PayloadAction<string>) {
    try {
        yield put(appActions.setLoading(Loading.App));

        yield call(vacancyApi.closeVacancy, vacancyId);
        window.location.href = '/vacancy';
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* openVacancy({ payload: vacancyId }: PayloadAction<string>) {
    try {
        yield put(appActions.setLoading(Loading.App));

        yield call(vacancyApi.openVacancy, vacancyId);
        yield put(
            appActions.enqueueSnackbar({
                id: uuid(),
                message: __t('Вакансия успешно опубликована'),
                icon: 'circle-check-duonote',
                iconColor: 'success',
            }),
        );
        yield put(vacancyActions.openVacancySuccess());
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* renewVacancy({ payload: vacancyId }: PayloadAction<string>) {
    try {
        yield put(appActions.setLoading(Loading.App));
        yield call(vacancyApi.renewVacancy, vacancyId);
        yield put(
            appActions.enqueueSnackbar({
                id: uuid(),
                message: __t('Вакансия успешно обновлена'),
                icon: 'circle-check-duonote',
                iconColor: 'success',
            }),
        );
        yield put(vacancyActions.renewVacancySuccess(new Date()));
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* getRecommendations({ payload }: PayloadAction<string>) {
    try {
        yield put(appActions.setLoading(Loading.App));
        const { data }: ApiResponse<RecommendationsWithProfiles> = yield call(vacancyApi.getRecommendations, payload);
        yield put(vacancyActions.getRecommendationsSuccess(data));
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* recommendedWorkerFavoritesToggle({ payload }: PayloadAction<{ id: number; value: boolean }>) {
    try {
        yield put(appActions.setLoading(Loading.App));
        yield call(workerApi.favoritesToggle, payload.id, payload.value);
        yield put(
            vacancyActions.recommendedWorkerFavoritesToggleSuccess({
                id: payload.id,
                value: payload.value,
                type: 'workers',
            }),
        );
        yield put(
            appActions.enqueueSnackbar({
                id: uuid(),
                message: payload.value ? __t('Анкета добавлена в избранное') : __t('Анкета удалена из избранного'),
                icon: 'circle-check-duonote',
                iconColor: 'success',
            }),
        );
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* inviteWorkerForVacancy({ payload }: PayloadAction<{ vacancyId: number; workerId: number }>) {
    try {
        yield put(appActions.setLoading(Loading.WorkerInvitation));
        yield call(vacancyApi.inviteWorkerForVacancy, payload.vacancyId, payload.workerId);
        yield put(vacancyActions.inviteWorkerForVacancySuccess(payload.vacancyId, payload.workerId));
        yield put(
            appActions.enqueueSnackbar({
                id: uuid(),
                message: __t('Запрошення відправлено'),
                icon: 'circle-check-duonote',
                iconColor: 'success',
            }),
        );
    } catch (e) {
        if (e instanceof ApiError && e.errors && !isEmptyObject(e.errors)) {
            yield put(vacancyActions.setWorkerInvitationErrors(e.errors));
            return;
        }
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

export function* vacancySaga() {
    yield takeEvery(vacancyActions.init, init);
    yield takeEvery(vacancyActions.getVacancyDataRequest, getVacancyData);
    yield takeEvery(vacancyActions.closeVacancyRequest, closeVacancy);
    yield takeEvery(vacancyActions.openVacancyRequest, openVacancy);
    yield takeEvery(vacancyActions.renewVacancyRequest, renewVacancy);
    yield takeEvery(vacancyActions.getRecommendationsRequest, getRecommendations);
    yield takeEvery(vacancyActions.recommendedWorkerFavoritesToggleRequest, recommendedWorkerFavoritesToggle);
    yield takeEvery(vacancyActions.inviteWorkerForVacancy, inviteWorkerForVacancy);
}
